import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import Card from "../../components/shared/Card/Card";
import Divider from "../../components/shared/Divider/Divider";
import { MdFolder } from "react-icons/md";
import Checkbox from "../../components/shared/Checkbox";
import Button from "../../components/shared/Button/Button";
import { useAllSwingCollectionsByUserId } from "./hooks/useAllSwingCollectionsByUserId";
import { InputRenameCollection } from "./InputRenameCollection";
import Tooltip from "../../components/shared/Tooltip/Tooltip";
import { COLLECTIONS_JOIN_SYMBOL_URL } from "./constants/collectionsJoinSymbolUrl";

export default function Collections() {
  const history = useHistory();
  const { collections } = useAllSwingCollectionsByUserId();

  const [selected, setSelected] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const compareDisabled = useMemo(() => selected.length < 2, [selected]);

  return (
    <div className="custom-margin-top-20 w-full p-5">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-gray900 text-lg font-semibold">Collections</h1>

        <Button
          color="primary500"
          isDisabled={compareDisabled}
          onClick={() =>
            history.push(
              `/compare?ids=${selected.join(COLLECTIONS_JOIN_SYMBOL_URL)}`,
            )
          }
        >
          Compare
        </Button>
      </div>

      <Divider className="my-8" color="gray200" type="horizontal" />

      {!collections || collections?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-gray700 text-lg font-medium">
            No collections yet
          </div>
          <div className="text-gray500 text-sm">
            Create your first collection from a swing’s actions menu.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {collections?.map((item) => {
            const updated = item.updatedAt || item.createdAt;
            const swingsCount = Array.isArray(item.videoIds)
              ? item.videoIds.length
              : 0;
            const isEditing = editingId === item.id;
            const isDisabledSelecting =
              !selected.includes(item.id) && selected?.length === 2;

            return (
              <Card
                borderColor="gray200"
                className="relative flex flex-col gap-2 p-6"
                color={selected.includes(item.id) ? "gray50" : "white"}
                isDisabled={isDisabledSelecting}
                key={item.id}
                onClick={
                  isEditing
                    ? undefined
                    : () =>
                        history.push(
                          `/collections/${encodeURIComponent(item.id)}`,
                        )
                }
                rounded="xl"
              >
                <Tooltip
                  content={
                    isDisabledSelecting
                      ? "You can select up to 2 collections"
                      : null
                  }
                >
                  <span
                    className="absolute top-3 right-3 w-fit"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Checkbox
                      isChecked={selected.includes(item.id)}
                      isDisabled={isDisabledSelecting}
                      onChange={() => {
                        toggleSelect(item.id);
                      }}
                    />
                  </span>
                </Tooltip>

                {/* folder icon */}
                <MdFolder className="text-blueLight300 size-6" />

                {/* title or editor */}
                {!isEditing ? (
                  <div className="flex flex-col gap-1">
                    <div className="text-gray700 truncate text-lg font-semibold">
                      {item.collectionName}
                    </div>

                    <div
                      className="w-fit text-primary700 cursor-pointer text-xs font-semibold underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingId(item.id);
                      }}
                    >
                      Rename
                    </div>
                  </div>
                ) : (
                  <InputRenameCollection
                    collection={item}
                    onCancel={() => setEditingId(null)}
                    onSubmit={() => setEditingId(null)}
                    size="xxs"
                  />
                )}

                <div className="text-gray900 text-base font-semibold">
                  {swingsCount} {swingsCount === 1 ? "swing" : "swings"}
                </div>

                <div className="text-grayTrue600 text-xs font-semibold">
                  Last updated {updated ? moment(updated).fromNow() : "—"}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
